import React from "react";
import axios from "axios";
import CalendarIcon from "./CalendarIcon.js";
import { analyticsHandler } from "./eventLogger.js";

const MODULE_NAME = "StickyNav";

// Items displayed in the popup menu
const menuItems = [
    { label: "Intra day", icon: "📊", screen: "pg_monitor", subcomponent: "Monitor" },
    { label: "Trades", icon: "📝", screen: "pg_tags", subcomponent: "Trades" }, // Changed icon here
    { label: "Options", icon: "Θ", screen: "pg_options", subcomponent: "Options" }, // Changed icon here
    { label: "Futures", icon: "⛽", screen: "pg_futures", subcomponent: "Futures", adminOnly: true }, // Changed icon here

    { label: "Calendar", icon: "📅", screen: "pg_compare", subcomponent: "Calendar-compare" },
    { label: "Upload", icon: "📤", screen: "pg_upload", subcomponent: "Upload", blockedAccountTypes: ["demo"] },
    { label: "Volatility Calculator", icon: "➗", screen: "pg_plan", subcomponent: "Plan" },
    { label: "Patterns", icon: "🧩", screen: "pg_patterns", subcomponent: "Patterns" },
    { label: "Labs", icon: "🔬", screen: "pg_labs", subcomponent: "Labs", adminOnly: true }
];

// Items always visible at the bottom
const persistentItems = [
    { label: "Menu", icon: "☰", isMenu: true },
    { label: "Recap", icon: "📝", screen: "pg_details", subcomponent: "Recap" }, // Changed icon here
    { label: "Patterns", icon: "🧩", screen: "pg_patterns", subcomponent: "Patterns" }
];

function getDeviceIsDesktop() {
    return (window.innerWidth >= 1024 && window.innerHeight >= 768) ? 1 : 0;
}

function StickyNav({ onNavigate, currentScreen, disabled }) {
    const [error, setError] = React.useState("");
    const [menuOpen, setMenuOpen] = React.useState(false);
    const menuRef = React.useRef(null);

    const accountType = window.sessionStorage.getItem("account_type");

    // Filter menu items based on account type and permissions
    const filteredMenuItems = menuItems.filter(item => {
        if (item.adminOnly && accountType !== "admin") {
            return false;
        }
        if (item.blockedAccountTypes && item.blockedAccountTypes.includes(accountType)) {
            return false;
        }
        return true;
    });

    // Close menu when clicking outside
    React.useEffect(() => {
        const handleClickOutside = (event) => {
            if (menuRef.current && !menuRef.current.contains(event.target)) {
                setMenuOpen(false);
            }
        };

        if (menuOpen) {
            document.addEventListener("mousedown", handleClickOutside);
            return () => document.removeEventListener("mousedown", handleClickOutside);
        }
    }, [menuOpen]);

    const handleNavigation = (screen) => {
        if (disabled) return;

        const currentScreenValue = sessionStorage.getItem("currentScreen") || "unknown";

        analyticsHandler({
            event_type: "button_click",
            module_name: MODULE_NAME,
            subcomponent: MODULE_NAME,
            screen: currentScreenValue
        })({});

        try {
            onNavigate(screen);
            setMenuOpen(false);
        } catch (err) {
            setError(err.message || err.toString());
        }
    };

    const handleMenuClick = () => {
        if (!disabled) {
            setMenuOpen(!menuOpen);
        }
    };

    return (
        <>
            {/* Popup Menu Overlay */}
            {menuOpen && (
                <div
                    style={{
                        position: "fixed",
                        bottom: 0,
                        left: 0,
                        width: "100%",
                        height: "100%",
                        backgroundColor: "rgba(0, 0, 0, 0.5)",
                        zIndex: 999,
                        animation: "fadeIn 0.2s ease-in-out"
                    }}
                    onClick={() => setMenuOpen(false)}
                />
            )}

            {/* Rollout Menu */}
            <div
                ref={menuRef}
                style={{
                    position: "fixed",
                    bottom: 120, // Adjusted position
                    left: 0, // Left-aligned
                    width: "400px", // Fixed width for the menu
                    maxHeight: menuOpen ? "500px" : "0", // Increased max height
                    backgroundColor: menuOpen ? "#222" : "transparent", // Background only when open
                    borderTop: menuOpen ? "1px solid #457b9d" : "none", // Border only when open
                    boxShadow: menuOpen ? "0 -4px 12px rgba(0,0,0,0.3)" : "none", // Shadow only when open
                    overflowY: "auto", // Scrollable if needed
                    overflowX: "hidden",
                    transition: "max-height 0.3s ease-in-out, background-color 0.3s, border-top 0.3s, box-shadow 0.3s",
                    zIndex: 1001,
                    display: "flex",
                    flexDirection: "column"
                }}
            >
                {filteredMenuItems.map((item) => (
                    <button
                        key={item.label}
                        onClick={() => handleNavigation(item.screen)}
                        style={{
                            flex: 1,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "flex-start",
                            padding: "12px 20px",
                            border: "none",
                            backgroundColor: currentScreen === item.screen ? "rgba(0, 255, 255, 0.1)" : "transparent",
                            color: currentScreen === item.screen ? "#00FFFF" : "inherit",
                            fontSize: "14px",
                            fontWeight: 500,
                            cursor: disabled ? "not-allowed" : "pointer",
                            transition: "background-color 0.2s, color 0.2s",
                            borderLeft: currentScreen === item.screen ? "3px solid #00FFFF" : "3px solid transparent"
                        }}
                        onMouseEnter={(e) => {
                            if (!disabled && currentScreen !== item.screen) {
                                e.target.style.backgroundColor = "rgba(255, 255, 255, 0.05)";
                            }
                        }}
                        onMouseLeave={(e) => {
                            if (currentScreen !== item.screen) {
                                e.target.style.backgroundColor = "transparent";
                            }
                        }}
                        disabled={disabled}
                    >
                        <span style={{ fontSize: 18, marginRight: 12 }}>
                            {item.icon}
                        </span>
                        <span>{item.label}</span>
                    </button>
                ))}
            </div>

            {/* Bottom Navigation Bar */}
            <nav
                className="sticky-nav"
                style={{
                    position: "fixed",
                    bottom: 0,
                    left: 0,
                    width: "100%",
                    zIndex: 1000,
                    background: "#222",
                    boxShadow: "0 -2px 8px rgba(0,0,0,0.12)",
                    borderTop: "none",
                    paddingBottom: 4,
                    display: "flex",
                    justifyContent: "center",
                    gap: "20px"
                }}
            >
                {persistentItems.map((item) => {
                    if (item.isMenu) {
                        return (
                            <button
                                key={item.label}
                                className="sticky-nav-item"
                                title={item.label}
                                onClick={handleMenuClick}
                                disabled={disabled}
                                style={{
                                    background: "none",
                                    border: menuOpen ? "2px solid #00FFFF" : "none",
                                    color: menuOpen ? "#00FFFF" : "inherit",
                                    cursor: disabled ? "not-allowed" : "pointer",
                                    display: "flex",
                                    flexDirection: "column",
                                    alignItems: "center",
                                    fontSize: "15px",
                                    fontWeight: 500,
                                    padding: "4px 12px",
                                    borderRadius: 16,
                                    transition: "border 0.2s, color 0.2s",
                                    opacity: disabled ? 0.5 : 1
                                }}
                                id="sticky-nav-menu"
                                data-analytics={MODULE_NAME}
                            >
                                <span
                                    style={{
                                        fontSize: 24,
                                        marginBottom: 2,
                                        filter: menuOpen ? "none" : "grayscale(1)",
                                        transition: "filter 0.2s"
                                    }}
                                >
                                    {item.icon}
                                </span>
                                <span className="sticky-nav-label" style={{ fontSize: 13 }}>
                                    {item.label}
                                </span>
                            </button>
                        );
                    }

                    const isActive = item.screen === currentScreen;
                    return (
                        <button
                            key={item.label}
                            className="sticky-nav-item"
                            title={item.label}
                            onClick={() => handleNavigation(item.screen)}
                            disabled={disabled}
                            style={{
                                background: "none",
                                border: isActive ? "2px solid #00FFFF" : "none",
                                color: isActive ? "#00FFFF" : "inherit",
                                cursor: disabled ? "not-allowed" : "pointer",
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                                fontSize: "15px",
                                fontWeight: 500,
                                padding: "4px 12px",
                                borderRadius: 16,
                                transition: "border 0.2s, color 0.2s",
                                opacity: disabled ? 0.5 : 1
                            }}
                            id={`sticky-nav-${item.screen}`}
                            data-analytics={MODULE_NAME}
                        >
                            <span
                                style={{
                                    fontSize: 24,
                                    marginBottom: 2,
                                    filter: isActive ? "none" : "grayscale(1)",
                                    transition: "filter 0.2s"
                                }}
                            >
                                {item.icon}
                            </span>
                            <span className="sticky-nav-label" style={{ fontSize: 13 }}>
                                {item.label}
                            </span>
                        </button>
                    );
                })}
            </nav>
        </>
    );
}

function formatDateMMDDYY(dateStr) {
    if (!dateStr || !/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) return "";
    const [year, month, day] = dateStr.split("-");
    return `${month}/${day}/${year.slice(-2)}`;
}

export default StickyNav;